import { createSelector } from 'reselect'
import { createIntl } from '../i18n/intl'

const getIntlState = state => state?.intl

const equalsIgnoreCase = (s1, s2) => s1.toUpperCase() === s2.toUpperCase()

/**
 * TDOD: Temporarily copied from rest-duckies to avoid cyclic dependencies.
 * Must be remove with MODUX-192.
 */
const selectUserSettings = createSelector([state => state.effectiveSettings], (settings = {}) => {
    return Object.values(settings).reduce((acc, current) => {
        if (equalsIgnoreCase(current.type, 'USER')) {
            acc[current.name] = {
                name: current.name,
                type: current.type.toUpperCase(),
                values: current.values
            }
        }
        return acc
    }, {})
})

/**
 * TDOD: Temporarily copied from rest-duckies to avoid cyclic dependencies.
 * Must be remove with MODUX-192.
 */
function getSingleValueSettings(settings, names) {
    return names.reduce((acc, name) => {
        const values = settings?.[name]?.values
        if (values != null) {
            acc[name] = values[0]
        }
        return acc
    }, {})
}

/**
 * Creates a selector for the intl object with constant distance, fuel efficiency unit, energy efficiency unit, temperature unit and coordinate format
 */
// eslint-disable-next-line max-params
export function createIntlSelector(
    distUnit = 'km',
    fuelEfficiencyUnit = 'l/100km',
    energyEfficiencyUnit = 'kWh/100km',
    pressureUnit = 'bar',
    temperatureUnit = 'Celsius',
    coordinateFormat = 'DMS'
) {
    return createSelector(getIntlState, intl => {
        const { locale, translations } = intl

        return createIntl(locale, {
            distUnit,
            fuelEfficiencyUnit,
            energyEfficiencyUnit,
            pressureUnit,
            temperatureUnit,
            coordinateFormat,
            translations
        })
    })
}

/**
 * Creates a memoized selector, which can be used to inject the Intl object to React components via
 * mapStateToProps(...).
 */
const getIntlObject = createSelector(getIntlState, selectUserSettings, (intl, settings = {}) => {
    const { locale, translations } = intl || {}
    const {
        CoordinateFormat = 'DMS',
        DistUnit = 'km',
        EnergyEfficiencyUnit = 'kWh/100km',
        FuelEfficiencyUnit = 'l/100km',
        PressureUnit = 'bar',
        TemperatureUnit = 'Celsius'
    } = getSingleValueSettings(settings, [
        'CoordinateFormat',
        'DistUnit',
        'FuelEfficiencyUnit',
        'EnergyEfficiencyUnit',
        'PressureUnit',
        'TemperatureUnit'
    ])

    return createIntl(locale, {
        coordinateFormat: CoordinateFormat,
        distUnit: DistUnit,
        pressureUnit: PressureUnit,
        fuelEfficiencyUnit: FuelEfficiencyUnit,
        energyEfficiencyUnit: EnergyEfficiencyUnit,
        temperatureUnit: TemperatureUnit,
        translations
    })
})

export default getIntlObject
